import { memoryChapterMapper } from '../mappers/memory-chapter-mapper';
import { memoryChatMapper } from '../mappers/memory-chat-mapper';
import { memoryMapper } from '../mappers/memory-mapper';
import { DEMO_CHAPTERS } from '../shared/memory-keeper-demo-script';

/* DEMO: real API-backed implementation, disabled while memory-keeper-demo-script.js hardcodes the flow.
import { apiClient } from '../shared/api-client';

const memoryRepository = {
    async getChapters() {
        const data = await apiClient.get({ url: 'memory/chapters' });

        return data.map(memoryChapterMapper.toDomain);
    },

    async openChat(chapter) {
        const data = await apiClient.post({ url: `memory/chapters/${chapter}/chat` });

        return memoryChatMapper.toDomain(data);
    },

    async submitAnswer(chapter, answer) {
        const data = await apiClient.post({
            url: `memory/chapters/${chapter}/answers`,
            body: { answer }
        });

        return memoryChatMapper.toDomain(data);
    },

    async getMemory(chapter) {
        const data = await apiClient.get({ url: `memory/chapters/${chapter}/memory` });

        return memoryMapper.toDomain(data);
    },

    async getMemories() {
        const data = await apiClient.get({ url: 'memory/memories' });

        return data.map(memoryMapper.toDomain);
    }
};
*/

const ANSWERS_PER_CHAPTER = 5;
const MEMORY_NOT_READY_ERROR = { statusCode: 404 };

const chapterState = {};

DEMO_CHAPTERS.forEach((script) => {
    chapterState[script.chapter] = {
        answerCount: 0,
        answers: [],
        messages: [],
        isChatFinished: false,
        isMemoryUnlocked: false
    };
});

function findChapterScript(chapter) {
    return DEMO_CHAPTERS.find((script) => script.chapter === chapter);
}

function buildBotMessage(chapter, questionIndex) {
    return { role: 'assistant', content: findChapterScript(chapter).questions[questionIndex] };
}

function buildMemoryData(chapter) {
    const script = findChapterScript(chapter);
    const state = chapterState[chapter];

    return {
        id: chapter,
        chapter,
        chapterTitle: script.title,
        title: script.memoryTitle,
        content: script.buildMemory(state.answers),
        answerCount: state.answerCount
    };
}

const memoryRepository = {
    async getChapters() {
        return DEMO_CHAPTERS.map((script) => {
            const state = chapterState[script.chapter];

            return memoryChapterMapper.toDomain({
                chapter: script.chapter,
                title: script.title,
                answerCount: state.answerCount,
                answersUntilMemory: Math.max(ANSWERS_PER_CHAPTER - state.answerCount, 0),
                isMemoryUnlocked: state.isMemoryUnlocked,
                isMemoryPending: false,
                isInitial: script.isInitial
            });
        });
    },

    async openChat(chapter) {
        const state = chapterState[chapter];

        if (!state.messages.length) {
            state.messages.push(buildBotMessage(chapter, 0));
        }

        return memoryChatMapper.toDomain({ messages: state.messages, isChatFinished: state.isChatFinished });
    },

    async submitAnswer(chapter, answer) {
        const state = chapterState[chapter];
        const script = findChapterScript(chapter);

        state.messages.push({ role: 'user', content: answer });
        state.answers.push(answer);
        state.answerCount += 1;

        if (state.answerCount >= ANSWERS_PER_CHAPTER) {
            state.isMemoryUnlocked = true;
        }

        if (state.answerCount < script.questions.length) {
            state.messages.push(buildBotMessage(chapter, state.answerCount));
        } else {
            state.isChatFinished = true;
        }

        return memoryChatMapper.toDomain({ messages: state.messages, isChatFinished: state.isChatFinished });
    },

    async getMemory(chapter) {
        if (!chapterState[chapter].isMemoryUnlocked) {
            throw MEMORY_NOT_READY_ERROR;
        }

        return memoryMapper.toDomain(buildMemoryData(chapter));
    },

    async getMemories() {
        return DEMO_CHAPTERS
            .filter((script) => chapterState[script.chapter].isMemoryUnlocked)
            .map((script) => memoryMapper.toDomain(buildMemoryData(script.chapter)));
    }
};

export {
    memoryRepository
};
